import { useTranslation } from 'react-i18next'

/**
 * How an invoice amount and status are shown, shared by the Invoices tab and
 * the overview's recent-invoices list so the two never disagree.
 */

/** Stripe reports minor units and a lower-case ISO 4217 code. */
export function formatMoney(cents: number, currency: string, locale?: string): string {
  const code = currency.toUpperCase()
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(cents / 100)
  } catch {
    // An unknown currency code throws; the figure still has to be shown.
    return `${(cents / 100).toFixed(2)} ${code}`
  }
}

// Stripe's own statuses. A status ramp means "paid / owed / lost / cancelled",
// not the brand, so each arm carries its own dark step.
const STATUS_STYLES: Record<string, string> = {
  paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  open: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  uncollectible: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  void: 'bg-muted text-muted-foreground',
}

export function InvoiceStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('dashboard')
  const style = STATUS_STYLES[status] ?? STATUS_STYLES.void
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style}`}>
      {t(`billing.invoiceStatus_${status}`, { defaultValue: status })}
    </span>
  )
}
